import { Link, Outlet, useLocation } from "react-router-dom"
import { MessageSquare } from "lucide-react"
import { PortalSidebar } from "@/components/PortalSidebar"
import { Toaster } from "@/components/portal/Toast"
import { MinimizedDraftsBar } from "@/components/MinimizedDraftsBar"
import { GlobalSearch } from "@/components/GlobalSearch"
import { InterneAufgabeKnopf } from "@/components/InterneAufgabeKnopf"
import { ProblemMeldenKnopf } from "@/components/ProblemMelden"
import { LiveCallBanner } from "@/components/LiveCallBanner"
import { StoerungsBanner } from "@/components/StoerungsBanner"
import { NeueFassungBanner } from "@/components/NeueFassungBanner"
import { useEventAlerts } from "@/hooks/useEventAlerts"
import { useEffect } from "react"
import { wegeAnmelden, wegSchritt } from "@/lib/wege"

/**
 * Haupt-Layout. Für E-Mails-Liste (/emails): full-bleed ohne Padding
 * damit das 2-Pane-ev2-Layout die volle Hoehe nutzen kann.
 * Ansonsten: Standard .pp-main mit 32/40px Padding.
 */
export default function AppLayout() {
  const loc = useLocation()
  useEventAlerts()

  // Bedienschritte sammeln (#44). Einmal anmelden, damit beim Verlassen der
  // Seite nachgereicht wird, was noch aussteht.
  useEffect(() => { wegeAnmelden() }, [])
  // Ein Seitenwechsel ist selbst ein Schritt — ohne ihn liest man aus der
  // Abfolge nicht heraus, wo jemand hin und her springt.
  useEffect(() => { wegSchritt("seite_gewechselt") }, [loc.pathname])  // globaler Event-Watcher: Ton + Tab/Favicon-Badge + Toast bei neuen Anrufen
  // emails-list (ohne /:id) ist full-bleed
  const isEmailsList = loc.pathname === "/emails" || loc.pathname === "/app/emails"
  // Review hat eigenen kompakten Header → weniger Top-Padding
  const isReview = loc.pathname.startsWith("/review") || loc.pathname.startsWith("/app/review")
  const mainCls = isEmailsList ? "pp-main-full" : `pp-main${isReview ? " pp-main-compact" : ""}`
  const isChat = loc.pathname.startsWith("/chat") || loc.pathname.startsWith("/app/chat")
  return (
    <>
      <PortalSidebar />
      <Toaster />
      {/* Globale Suche oben rechts — auf E-Mails-List versteckt (eigenes UI) */}
      {!isEmailsList && <GlobalSearch />}
      {/* Interne Aufgabe von ueberall anlegen (#33). Steht neben der Lupe und
          wird mit ihr zusammen versteckt — auf der Mailliste ist oben rechts
          kein Platz, dort hat die Seite ihre eigene Leiste. */}
      {!isEmailsList && <InterneAufgabeKnopf />}
      {/* „Problem melden" (Baustein 17): neben „Interne Aufgabe". Auf der
          Mailliste schwebend über der Chat-Blase — dort hat die Seite ihre
          eigene Kopfleiste, und gerade dort wird am meisten gemeldet. */}
      <ProblemMeldenKnopf schwebend={isEmailsList} />
      {/* Live-Anzeige aktiver Anrufe — global, auf jeder Seite sichtbar */}
      <LiveCallBanner />
      {/* Plattform-Stoerungen (Telefonbot down etc.) — rot, oben, ueberall */}
      <StoerungsBanner />
      {/* 🔴 Alte Fassung im offenen Tab: dreimal als „mails verschwinden"
          gemeldet, obwohl der Fehler laengst behoben war. Siehe
          `lib/neueFassung.ts`. */}
      <NeueFassungBanner />
      <main className={mainCls}>
        <Outlet />
      </main>
      {/* Globale Pill-Bar fuer minimierte Modal-Entwuerfe (Kalender, Kontakt) */}
      <MinimizedDraftsBar />
      {/* Floating Chat-Icon — 1:1 wie altes Portal */}
      {!isChat && (
        <Link
          to="/chat"
          title="Assistent öffnen"
          aria-label="Assistent öffnen"
          style={{
            position: "fixed", right: 24, bottom: 24, zIndex: 40,
            width: 48, height: 48, borderRadius: "50%",
            background: "var(--accent-color, #4f46e5)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 6px 20px rgba(79,70,229,0.35)",
            textDecoration: "none",
          }}
          // `vollhoehe`: auf dem Laptop ausgeblendet, siehe admin-theme.css (#96)
          className={`pp-chat-blase${isEmailsList || isReview ? " vollhoehe" : ""} hover:scale-105 transition`}
        >
          <MessageSquare className="w-5 h-5" />
        </Link>
      )}
    </>
  )
}
